import type { PermissionCode } from '@/lib/permissions'

/**
 * The Access module's administration surface: staff accounts, roles, the permission catalogue,
 * and the field devices hanging off an account.
 *
 * Two conversions happen at this boundary and nowhere else, the same two `approvals-types.ts`
 * documents for the rows the two files share:
 *
 *   · identifiers arrive as numbers on some endpoints and strings on others — `branchId`
 *     throughout, `DeviceListItem.userId` — and are strings everywhere this application uses one;
 *   · a user's `status` crosses the wire as a bare integer with no enum metadata anywhere in the
 *     OpenAPI document. `1 Active, 2 Locked, 3 Disabled` is not a guess: it is the same field
 *     `ApprovalStaffMemberPayload.status` already names it as in `approvals-types.ts`, because both
 *     read rows off the same `/access/users` endpoint.
 *
 * `DevicePlatform` and `DeviceStatus` get no such mapping, deliberately. Nothing in the published
 * contract or the rest of this codebase says what their integers mean. The audit-source precedent
 * in `approvals-api.ts` is to name an *unrecognised* value "Unknown" rather than assign it a
 * plausible-sounding one; here every value is unrecognised, so the raw number is what a screen
 * shows, honestly, rather than an invented label with a coin-flip chance of being right.
 */

/** `1 Active, 2 Locked, 3 Disabled` — see the file header. An unlisted value renders as `Unknown`. */
export type UserStatus = 'Active' | 'Locked' | 'Disabled' | 'Unknown'

/** The inverse of the read-side mapping, for the one request that sends a status back. */
export const USER_STATUS_CODES: Record<Exclude<UserStatus, 'Unknown'>, number> = {
  Active: 1,
  Locked: 2,
  Disabled: 3,
}

/* ─── Staff accounts ─────────────────────────────────────────────────────────────────────── */

/** One row of the staff directory, exactly as sent. */
export interface UserListItemPayload {
  userId: string
  username: string
  fullName: string
  employeeCode: string
  branchId: number | string
  status: number
  isLoanOfficer: boolean
  isTeller: boolean
  mfaEnrolled: boolean
  lockedOut: boolean
  lastLoginUtc: string | null
  roles: string[]
}

/** One row of the staff directory, after normalisation. */
export interface UserListItem {
  userId: string
  username: string
  fullName: string
  employeeCode: string
  branchId: string
  status: UserStatus
  isLoanOfficer: boolean
  isTeller: boolean
  mfaEnrolled: boolean
  lockedOut: boolean
  /** Null for an account that has never signed in. */
  lastLoginUtc: string | null
  roles: string[]
}

/** As sent — like `ApprovalStaffPage` and every paged list here bar the shared `PagedResult`, no `totalPages`. */
export interface UserPagePayload {
  items: UserListItemPayload[]
  page: number | string
  pageSize: number | string
  totalCount: number | string
}

export interface UserPage {
  items: UserListItem[]
  page: number
  pageSize: number
  totalCount: number
}

/** Branch access beyond the home branch, as sent. */
export interface UserBranchGrantPayload {
  branchId: number | string
  expiresAtUtc: string | null
}

export interface UserBranchGrant {
  branchId: string
  /** Null for a grant with no expiry. */
  expiresAtUtc: string | null
}

/** A field device on one user's account, as sent — scoped to the user, unlike `DeviceListItem` below. */
export interface UserDeviceSummaryPayload {
  deviceId: string
  deviceUid: string
  platform: number
  status: number
  registeredAtUtc: string
  lastSyncUtc: string | null
}

/** A field device on one user's account. `platform` and `status` are shown raw — see the file header. */
export interface UserDeviceSummary {
  deviceId: string
  deviceUid: string
  platform: number
  status: number
  registeredAtUtc: string
  /** Null for a device that has never synced since it was registered. */
  lastSyncUtc: string | null
}

/** The full staff record, exactly as sent. */
export interface UserDetailPayload {
  userId: string
  username: string
  fullName: string
  employeeCode: string
  email: string | null
  phoneNumber: string | null
  branchId: number | string
  status: number
  isLoanOfficer: boolean
  isTeller: boolean
  mustChangePassword: boolean
  mfaEnrolled: boolean
  mfaEnrolledAtUtc: string | null
  remainingRecoveryCodes: number | string
  lockedOut: boolean
  lockoutEndsAtUtc: string | null
  failedAccessAttempts: number | string
  lastLoginUtc: string | null
  createdAtUtc: string
  roles: string[]
  permissions: string[]
  additionalBranches: UserBranchGrantPayload[]
  devices: UserDeviceSummaryPayload[]
}

/** The full staff record, after normalisation. This is what every admin screen renders. */
export interface UserDetail {
  userId: string
  username: string
  fullName: string
  employeeCode: string
  email: string | null
  phoneNumber: string | null
  branchId: string
  status: UserStatus
  isLoanOfficer: boolean
  isTeller: boolean
  /** Forced at next sign-in — set the moment a temporary password is issued. */
  mustChangePassword: boolean
  mfaEnrolled: boolean
  mfaEnrolledAtUtc: string | null
  remainingRecoveryCodes: number
  lockedOut: boolean
  lockoutEndsAtUtc: string | null
  failedAccessAttempts: number
  lastLoginUtc: string | null
  createdAtUtc: string
  roles: string[]
  /** The account's effective permission set — the same shape a signed-in session carries. */
  permissions: PermissionCode[]
  additionalBranches: UserBranchGrant[]
  devices: UserDeviceSummary[]
}

/* ─── Commands over a user ───────────────────────────────────────────────────────────────── */

/**
 * `CreateUserCommand` on the wire also carries `approvalRequestType` and `approvalAmount`, both
 * optional. Neither is filled in here: they read as `IApprovableCommand`'s own properties leaking
 * into the request DTO rather than something an operator supplies, and every other dual-controlled
 * write in this application — `GroupWriteRequest`, `LoanProductRequest` — omits the equivalent
 * pair. The server derives the approval envelope itself; nothing this client could send would
 * change it.
 */
export interface CreateUserRequest {
  username: string
  fullName: string
  employeeCode: string
  email: string
  phoneNumber: string | null
  branchId: string
  isLoanOfficer: boolean
  isTeller: boolean
  roleIds: string[]
}

export interface UpdateUserRequest {
  fullName: string
  email: string
  phoneNumber: string | null
  branchId: string
  isLoanOfficer: boolean
  isTeller: boolean
}

export interface SetUserStatusRequest {
  status: UserStatus
  reason: string
}

export interface ResetUserMfaRequest {
  reason: string
}

export interface IssueTemporaryPasswordRequest {
  reason: string
}

/** As sent. */
export interface TemporaryPasswordPayload {
  userId: string
  username: string
  password: string
}

/**
 * A one-time password, shown exactly once.
 *
 * The server does not store this in the clear and there is no endpoint to read it back — a screen
 * that navigates away from it has shown it for the last time. See `temporary-password-dialog.tsx`.
 */
export interface TemporaryPassword {
  userId: string
  username: string
  password: string
}

export interface AssignUserRolesRequest {
  roleIds: string[]
  /** Written when a role combination the server flags as a segregation-of-duties conflict is chosen. */
  segregationJustification: string | null
}

/* ─── Roles and the permission catalogue ─────────────────────────────────────────────────── */

/** One row of the role register, exactly as sent. */
export interface RoleListItemPayload {
  roleId: string
  name: string
  description: string
  isSystemRole: boolean
  forbidsFinancialPermissions: boolean
  isBreakGlass: boolean
  userCount: number | string
  permissions: string[]
}

/**
 * One row of the role register.
 *
 * There is no `GET /access/roles/{roleId}` in the published contract — a role's detail is this row,
 * looked up by id out of the list this endpoint already returned. See `role-detail.tsx`.
 */
export interface RoleListItem {
  roleId: string
  name: string
  description: string
  /** A built-in role — SuperAdministrator and the like. This client does not offer to delete one. */
  isSystemRole: boolean
  /** Segregation of duties: a role marked this way may not also carry a permission that posts money. */
  forbidsFinancialPermissions: boolean
  /**
   * The sealed-envelope identity. Holding every permission, it breaches segregation of duties by
   * construction, so the server refuses it on any create-user request — the create form does not
   * offer it. Assigning it to an existing account is allowed and audited as a recorded bypass.
   */
  isBreakGlass: boolean
  userCount: number
  permissions: PermissionCode[]
}

export interface CreateRoleRequest {
  name: string
  description: string
  permissionCodes: PermissionCode[]
}

export interface UpdateRolePermissionsRequest {
  permissionCodes: PermissionCode[]
}

/** One entry in the permission catalogue. */
export interface PermissionListItem {
  code: PermissionCode
  module: string
  description: string
}

/** The catalogue, grouped by module — exactly the shape a role's permission editor needs. */
export interface PermissionModuleGroup {
  module: string
  permissions: PermissionListItem[]
}

/* ─── Devices, across the whole directory ────────────────────────────────────────────────── */

/**
 * A registered field device, as the cross-user admin listing sends it.
 *
 * `userId` here is a quoted int64, unlike every other user reference in this module —
 * `UserDetail.userId`, `UserListItem.userId`, and the `{userId}` path parameter on every user
 * route are all UUIDs. This is almost certainly a different underlying column (an internal numeric
 * id versus the Access module's external UUID) and the two must never be compared or joined on the
 * client. Revoking a device does not need the join: `POST /access/devices/{deviceId}/revoke` takes
 * only the device's own id.
 */
export interface DeviceListItemPayload {
  deviceId: string
  userId: number | string
  username: string
  deviceUid: string
  platform: number
  status: number
  model: string | null
  appVersion: string | null
  registeredAtUtc: string
  lastSyncUtc: string | null
  revokedReason: string | null
}

export interface DeviceListItem {
  deviceId: string
  /** Not this application's user id — see the file header. Shown, never joined against a `UserDetail`. */
  userId: string
  username: string
  deviceUid: string
  platform: number
  status: number
  model: string | null
  appVersion: string | null
  registeredAtUtc: string
  lastSyncUtc: string | null
  revokedReason: string | null
}

export interface RevokeDeviceRequest {
  reason: string
}
